import { memo, useState } from 'react'

import { AvcTask } from '@nativewaves/nw-cloud-sdk/content'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { PickerDialogContent } from '@shared/components/PickerDialog'
import { avcTaskQueryKeys } from 'services/queryKeys'

export type TaskPickerDialogContentProps = {
  /** Scopes the search to the stream's own repository. */
  repositoryId?: string
  /** Already-linked ids, disabled in the list so the same task can't be added twice. */
  linkedTaskIds: Set<string>
  /** Currently picked task, highlighted in the list. */
  taskId?: string
  onSelect: (task: AvcTask) => void
}

const TaskPickerDialogContentRaw = ({
  repositoryId,
  linkedTaskIds,
  taskId,
  onSelect,
}: TaskPickerDialogContentProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const [queryValue, setQueryValue] = useState<string>()

  const avcTasksQuery = useInfiniteQuery({
    ...avcTaskQueryKeys.list({ repositoryId, view: 'Slim', query: queryValue, limit: 50 }),
    enabled: !!repositoryId,
  })
  const avcTasks = avcTasksQuery.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <PickerDialogContent<AvcTask>
      items={avcTasks}
      listQuery={avcTasksQuery}
      onQueryChange={setQueryValue}
      searchPlaceholder={t('domain:Workspace.AvcTaskAssociations.selectTask')}
      emptyText={t('domain:Workspace.AvcTaskAssociations.noTasks')}
      getKey={(avcTask) => avcTask.id}
      getPrimary={(avcTask) => avcTask.name || `${t('id')} (${avcTask.id})`}
      getSecondary={(avcTask) =>
        linkedTaskIds.has(avcTask.id) ? t('domain:Workspace.AvcTaskAssociations.alreadyLinked') : avcTask.id
      }
      isSelected={(avcTask) => avcTask.id === taskId}
      isDisabled={(avcTask) => linkedTaskIds.has(avcTask.id)}
      onSelect={onSelect}
    />
  )
}

export const TaskPickerDialogContent = memo(TaskPickerDialogContentRaw)
